import { useAuth } from "../hooks/AuthProvider";
import { message } from "antd";

const useAuthRequest = () => {
  const auth = useAuth();
  let refreshPromise = null; // To prevent multiple simultaneous refresh attempts
  
  const makeRequest = async (url, options) => {
    try {
      // Add token to headers if needed
      if (options.headers.requiresToken !== false) {
        let token = '';
        if (auth.user) {
          token = auth.user.access_token;
          options.headers['Authorization'] = `${token}`;
        }
      }
  
      // Remove the custom requiresToken property
      delete options.headers.requiresToken;

      // Add credentials to include cookies
      options.credentials = 'include';
  
      // Make the initial request
      let response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/${url}`, options);
  
      // Parse the response data
      let responseData = await response.json();
      responseData.status = response.status;

      // Handle token expiration (jwt expired case)
      if (responseData.msg === 'jwt expired') {
        try {
          // If a refresh is already in progress, wait for it
          if (refreshPromise) {
            await refreshPromise;
            // Retry the request with the new token
            options.headers['Authorization'] = `${auth.user.access_token}`;
            response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/${url}`, options);
            responseData = await response.json();
            responseData.status = response.status;
          } else {
            // Start a new refresh
            refreshPromise = performTokenRefresh();
            await refreshPromise;
            refreshPromise = null;
            
            // Retry the request with the new token
            options.headers['Authorization'] = `${auth.user.access_token}`;
            response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/${url}`, options);
            responseData = await response.json();
            responseData.status = response.status;
          }
        } catch (refreshError) {
          console.error('Token refresh error:', refreshError);
          auth.logout();
          throw refreshError;
        }
      }
  
      return responseData;
    } catch (error) {
      // Ensure the error is logged and thrown correctly
      console.error('Fetch Request Error:', error);
      throw error; // Ensure the error is passed to the calling function
    }
  };

  const performTokenRefresh = async () => {
    try {
      // Attempt to refresh the token
      const refreshResponse = await fetch(`${process.env.REACT_APP_BASE_URL}/api/refresh_token`, {
        method: 'POST',
        credentials: 'include', // Include cookies for refresh token
        headers: { 'Content-Type': 'application/json' },
      });
      
      const refreshData = await refreshResponse.json();
      
      if (refreshResponse.ok) {
        // Update user data in localStorage and state
        const updatedUserData = {
          ...auth.user,
          access_token: refreshData.access_token,
          user: refreshData.user
        };
        localStorage.setItem('currentUser', JSON.stringify(updatedUserData));
        auth.setUser(updatedUserData);
      } else {
        // Refresh failed, logout user
        auth.logout();
        throw new Error(refreshData.msg || 'Token refresh failed');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      auth.logout();
      throw error;
    }
  };
  
  const getDataAPI = async (url) => {
    return await makeRequest(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const getDataAPIWithoutToken = async (url) => {
    return await makeRequest(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', requiresToken: false },
    });
  };

  const postDataAPI = async (url, post) => {
    return await makeRequest(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: post ? JSON.stringify(post) : null,
    });
  };

  const postDataAPIWithoutToken = async (url, post) => {
    const headers = post instanceof FormData
      ? {}  // No Content-Type header; allow the browser to set it
      : { 'Content-Type': 'application/json', requiresToken: false };

    return await makeRequest(url, {
      method: 'POST',
      headers: headers,
      body: post instanceof FormData ? post : JSON.stringify(post),
    });
  };

  const deleteDataAPI = async (url) => {
    return await makeRequest(url, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
    });
  };

  const patchDataAPI = async (url, post) => {
    return await makeRequest(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: post ? JSON.stringify(post) : null,
    });
  };

  const putDataAPI = async (url, post) => {
    return await makeRequest(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: post ? JSON.stringify(post) : null,
    });
  };

  return { getDataAPI, getDataAPIWithoutToken, postDataAPI, postDataAPIWithoutToken, deleteDataAPI, patchDataAPI, putDataAPI };
};

export default useAuthRequest;
